import {Camera, Mesh, PlaneGeometry, ShaderMaterial, Vector2, WebGLRenderTarget} from 'three'

import common from './common'
import shaders from './glsl/shaders'

export default class GaussianBlur {
  weight: number[]
  blurRadius: number
  vertical?: Mesh
  horizontal?: Mesh
  camera: Camera
  step: Vector2
  uniforms: {
    uStep: {value: Vector2}
    uWeight: {value: number[]}
  }
  defines: {
    BLUR_RADIUS: number
  }
  originalFbo?: WebGLRenderTarget
  blurFbos: WebGLRenderTarget[] = []
  constructor(originalFbo: WebGLRenderTarget, blurRadius: number = 12) {
    this.weight = []
    this.blurRadius = blurRadius

    this.camera = new Camera()
    this.step = new Vector2()

    this.uniforms = {
      uStep: {
        value: this.step,
      },
      uWeight: {
        value: this.weight,
      },
    }

    this.defines = {
      BLUR_RADIUS: this.blurRadius,
    }

    this.originalFbo = originalFbo
    this.init()
  }

  init() {
    if (!this.originalFbo) return
    this.blurFbos.push(
      new WebGLRenderTarget(this.originalFbo.width, this.originalFbo.height),
      new WebGLRenderTarget(this.originalFbo.width, this.originalFbo.height),
    )

    this.step.set(1 / this.originalFbo.width, 1 / this.originalFbo.height)

    this.makeWeight()

    this.vertical = new Mesh(
      new PlaneGeometry(2, 2),
      new ShaderMaterial({
        vertexShader: shaders.screenVert,
        fragmentShader: shaders.blurFrag,
        uniforms: {
          ...this.uniforms,
          uDiffuse: {
            value: this.originalFbo.texture,
          },
          uStepSize: {
            value: new Vector2(0.0, 1.0),
          },
        },
        defines: this.defines,
      }),
    )

    this.horizontal = new Mesh(
      new PlaneGeometry(2, 2),
      new ShaderMaterial({
        vertexShader: shaders.screenVert,
        fragmentShader: shaders.blurFrag,
        uniforms: {
          ...this.uniforms,
          uDiffuse: {
            value: this.blurFbos[0] ? this.blurFbos[0].texture : null,
          },
          uStepSize: {
            value: new Vector2(1.0, 0.0),
          },
        },
        defines: this.defines,
      }),
    )
  }

  makeWeight() {
    this.weight = []
    let t = 0.0

    for (let i = this.blurRadius - 1; i >= 0; i--) {
      const r = 1.0 + 2.0 * i
      let w = Math.exp((-0.5 * (r * r)) / (this.blurRadius * this.blurRadius))
      this.weight.push(w)
      if (i > 0) {
        w *= 2.0
      }
      t += w
    }

    for (let i = 0; i < this.weight.length; i++) {
      this.weight[i] /= t
    }

    this.uniforms.uWeight.value = this.weight
  }

  resize() {
    if (!this.originalFbo) return
    for (let i = 0; i < this.blurFbos.length; i++) {
      if (this.blurFbos[i]) {
        this.blurFbos[i]?.setSize(this.originalFbo.width, this.originalFbo.height)
      }
    }

    if (this.step) {
      this.step?.set(1 / this.originalFbo.width, 1 / this.originalFbo.height)
    }
  }

  update() {
    if (this.vertical && this.blurFbos[0]) {
      common.renderer?.setRenderTarget(this.blurFbos[0])
      common.renderer?.render(this.vertical, this.camera)
    }

    if (this.horizontal && this.blurFbos[1]) {
      common.renderer?.setRenderTarget(this.blurFbos[1])
      common.renderer?.render(this.horizontal, this.camera)
    }
  }
}
